/**
 * User portfolio manager that calculates balances, net worth, and aggregated APY.
 */
import { normalizeStructTag } from '@mysten/sui/utils';
import { Decimal } from 'decimal.js';
export class Portfolio {
    protocol;
    strategyContext;
    constructor(protocol, strategyContext) {
        this.protocol = protocol;
        this.strategyContext = strategyContext;
    }
    /** Get all coin balances in user's wallet. */
    async getWalletCoins(userAddress) {
        const res = await this.strategyContext.blockchain.getAllBalances(userAddress);
        const resMap = new Map();
        res.forEach((entry) => {
            resMap.set(normalizeStructTag(entry.coinType), entry.totalBalance);
        });
        return resMap;
    }
    async getAllPoolStrategies(userAddress) {
        const strategies = await this.protocol.getStrategies();
        await this.updateStrategiesWithReceipts(userAddress, strategies);
        return strategies;
    }
    async getPoolStrategy(userAddress, poolId) {
        const strategy = await this.protocol.getSinglePoolStrategy(poolId);
        await this.updateStrategiesWithReceipts(userAddress, new Map([[poolId, strategy]]));
        return strategy;
    }
    /** Calculate user's complete portfolio including net worth, aggregated APY, and alpha rewards. */
    async getUserPortfolio(userAddress, strategiesType) {
        const strategies = await this.protocol.getStrategies(strategiesType);
        await this.updateStrategiesWithReceipts(userAddress, strategies);
        // Fetch APR map once for all strategies
        const aprMap = await this.strategyContext.getAprMap();
        const balancesWithIds = await Promise.all(Array.from(strategies.entries()).map(async ([poolId, strategy]) => {
            const balance = await strategy.getBalance(userAddress);
            return [poolId, balance];
        }));
        const poolBalances = new Map(balancesWithIds);
        let [netWorth, aggregatedApy] = [new Decimal(0), new Decimal(0)];
        Array.from(poolBalances.entries()).forEach(([poolId, balance]) => {
            const balanceUsd = 'stakedAlphaUsdValue' in balance ? balance.stakedAlphaUsdValue : balance.usdValue;
            // Cap retired pool APY at 1000%
            const aprData = aprMap.get(poolId) || {
                baseApr: new Decimal(0),
                alphaMiningApr: new Decimal(0),
                apy: new Decimal(0),
                lastAutocompounded: new Date(),
            };
            let apy = new Decimal(aprData.apy);
            const isActive = strategies.get(poolId)?.getPoolLabel()?.isActive;
            if (isActive === false && apy.gt(1000)) {
                apy = new Decimal(1000);
            }
            netWorth = netWorth.add(balanceUsd);
            aggregatedApy = aggregatedApy.add(balanceUsd.mul(apy));
        });
        aggregatedApy = netWorth.isZero() ? new Decimal(0) : aggregatedApy.div(netWorth);
        // Sum alpha rewards across all strategies
        const distributor = await this.strategyContext.getDistributorObject();
        let alphaRewardsToClaim = new Decimal(0);
        for (const strategy of strategies.values()) {
            alphaRewardsToClaim = alphaRewardsToClaim.add(strategy.getAlphaMiningRewardsToClaim(distributor));
        }
        return { netWorth, aggregatedApy, alphaRewardsToClaim, poolBalances };
    }
    /** Update strategies with user's receipt objects and positions. */
    async updateStrategiesWithReceipts(userAddress, strategies) {
        const poolLabels = Array.from(strategies.values()).map((strategy) => strategy.getPoolLabel());
        const receiptTypes = [];
        let [hasSlushLending, hasFungible, hasAlphaVault] = [false, false, false];
        poolLabels.forEach((poolLabel) => {
            switch (poolLabel.strategyType) {
                case 'AlphaVault':
                    hasAlphaVault = true;
                    receiptTypes.push(poolLabel.receipt.type);
                    break;
                case 'AutobalanceLp':
                case 'Lending':
                case 'Looping':
                case 'Lp':
                case 'Lyf':
                case 'SingleAssetLooping':
                    receiptTypes.push(poolLabel.receipt.type);
                    break;
                case 'FungibleLp':
                    hasFungible = true;
                    break;
                case 'SlushLending':
                case 'SlushSingleAssetLooping':
                case 'SlushLooping':
                    hasSlushLending = true;
                    break;
                case 'FungibleLending':
                    hasFungible = true;
                    break;
                default:
                    break;
            }
        });
        const [slushPositions, alphafiPositions, receiptObjects, coinBalances] = await Promise.all([
            hasSlushLending
                ? this.strategyContext.getAllSlushPositions(userAddress)
                : Promise.resolve(new Map()),
            hasAlphaVault
                ? this.strategyContext.getPositionsFromAlphaFiReceipts(userAddress)
                : Promise.resolve(new Map()),
            this.strategyContext.blockchain.multiGetReceipts(userAddress, receiptTypes),
            hasFungible ? this.getWalletCoins(userAddress) : Promise.resolve(new Map()),
        ]);
        strategies.forEach((strategy, poolId) => {
            switch (strategy.getPoolLabel().strategyType) {
                case 'AlphaVault': {
                    const alphaVaultStrategy = strategy;
                    alphaVaultStrategy.updateReceipts(receiptObjects.get(alphaVaultStrategy.getPoolLabel().receipt.type) ?? [], alphafiPositions.get(poolId) ?? []);
                    break;
                }
                case 'AutobalanceLp': {
                    const autobalanceLpStrategy = strategy;
                    autobalanceLpStrategy.updateReceipts(receiptObjects.get(autobalanceLpStrategy.getPoolLabel().receipt.type) ?? []);
                    break;
                }
                case 'FungibleLp': {
                    const fungibleLpStrategy = strategy;
                    fungibleLpStrategy.updateReceipts(new Decimal(coinBalances.get(strategy.getPoolLabel().fungibleCoin.type) ?? '0'));
                    break;
                }
                case 'Lending': {
                    const lendingStrategy = strategy;
                    lendingStrategy.updateReceipts(receiptObjects.get(lendingStrategy.getPoolLabel().receipt.type) ?? []);
                    break;
                }
                case 'Looping': {
                    const loopingStrategy = strategy;
                    loopingStrategy.updateReceipts(receiptObjects.get(loopingStrategy.getPoolLabel().receipt.type) ?? []);
                    break;
                }
                case 'Lp': {
                    const lpStrategy = strategy;
                    lpStrategy.updateReceipts(receiptObjects.get(lpStrategy.getPoolLabel().receipt.type) ?? []);
                    break;
                }
                case 'Lyf': {
                    const lyfStrategy = strategy;
                    lyfStrategy.updateReceipts(receiptObjects.get(lyfStrategy.getPoolLabel().receipt.type) ?? []);
                    break;
                }
                case 'SingleAssetLooping': {
                    const singleAssetLoopingStrategy = strategy;
                    singleAssetLoopingStrategy.updateReceipts(receiptObjects.get(singleAssetLoopingStrategy.getPoolLabel().receipt.type) ?? []);
                    break;
                }
                case 'SlushLending': {
                    const slushLendingStrategy = strategy;
                    slushLendingStrategy.updateReceipts(slushPositions.get(poolId) ?? []);
                    break;
                }
                case 'SlushSingleAssetLooping': {
                    const slushStrategy = strategy;
                    slushStrategy.updateReceipts(slushPositions.get(poolId) ?? []);
                    break;
                }
                case 'SlushLooping': {
                    const slushLoopingStrategy = strategy;
                    slushLoopingStrategy.updateReceipts(slushPositions.get(poolId) ?? []);
                    break;
                }
                case 'FungibleLending': {
                    const fungibleLpStrategy = strategy;
                    fungibleLpStrategy.updateReceipts(new Decimal(coinBalances.get(strategy.getPoolLabel().fungibleCoin.type) ?? '0'));
                    break;
                }
            }
        });
    }
}
//# sourceMappingURL=portfolio.js.map